/**
 * Returns the Google Maps Places library, deferring to the APIProvider
 * (mounted in App.js) to load the SDK.
 *
 * APIProvider injects its bootstrap loader via useEffect, which runs
 * parent-after-child in React — meaning this utility may be called before
 * APIProvider's effect has run. We poll briefly for importLibrary to appear
 * (it is set synchronously when the bootstrap IIFE executes, typically within
 * one or two 50 ms ticks) and then import the "places" library through it.
 *
 * We intentionally do NOT inject our own bootstrap script here. Doing so
 * created a second competing loader that corrupted google.maps.Map.
 */
let loadPromise = null;

function waitForImportLibrary(maxMs = 10000) {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + maxMs;
    const check = () => {
      if (window.google?.maps?.importLibrary) return resolve();
      if (Date.now() > deadline) return reject(new Error('Google Maps API did not load in time'));
      setTimeout(check, 50);
    };
    setTimeout(check, 50);
  });
}

export function loadGoogleMapsScript(apiKey) {
  if (typeof window === 'undefined') return Promise.reject(new Error('Not in browser'));
  if (loadPromise) return loadPromise;
  if (!apiKey) return Promise.reject(new Error('Google Maps API key not set'));

  loadPromise = waitForImportLibrary()
    .then(() => window.google.maps.importLibrary('places'));

  return loadPromise;
}
